import type { ColumnLayoutEntry } from 'components/tables/list-table/columnLayout/types';

/** The list pages that hold saved views, named by their route; the backend's `ListKey` has the same strings. */
export const LIST_KEYS = [
    'matters',
    'projects',
    'contacts',
    'groups',
    'contracts',
    'marketing-profiles',
    'efforts',
    'rankings-referees',
    'experience-records',
    'law-firms',
    'debriefings',
    'correspondence',
    'compliance',
    'employees',
    'teams',
    'offices',
    'practice-areas',
    'trainings',
    'invoices',
    'billable-clients',
    'pricelists',
    'expenses',
    'prepayments',
    'bank-transfers',
    'tasks',
    'my-portfolio',
    'timecards',
] as const;

export type ListKey = (typeof LIST_KEYS)[number];

/** Where a list lives when its route is not `/<key>`. */
const LIST_PATHS: Partial<Record<ListKey, string>> = { 'marketing-profiles': '/contacts/marketing' };

/** The path of a list's page, the one the menu links to. */
export const listPath = (key: ListKey): string => LIST_PATHS[key] ?? `/${key}`;

/** The list a page path is, when it is one of {@link LIST_KEYS}' pages itself rather than a record tab. */
export const listKeyOfPath = (pathname: string): ListKey | undefined => {
    const path = pathname.replace(/\/+$/, '');
    return LIST_KEYS.find((key) => listPath(key) === path);
};

/** Whether `pathname` is the list's own page, or one of its views' (`/invoices/law`), rather than a record's tab. */
export const isListPage = (pathname: string, key: ListKey): boolean =>
    pathname === listPath(key) || pathname.startsWith(`${listPath(key)}/`);

/** The lists whose built-in views are segments of their page's path. */
const VIEW_PATH_LISTS: ListKey[] = ['invoices', 'timecards'];

/** The list's page showing one of its built-in views. */
export const listViewPath = (key: ListKey, viewId: string): string =>
    VIEW_PATH_LISTS.includes(key) ? `${listPath(key)}/${viewId}` : listPath(key);

export type ListViewVisibility = 'PRIVATE' | 'TEAM' | 'PEOPLE';

/** What a view keeps. `query` holds the list's `qf`, `mf` and `sort` exactly as its URL carries them. */
export type ListViewState = {
    v: 1;
    query: string;
    columns?: ColumnLayoutEntry;
};

export type ListViewPerson = { id: number; name: string };

export type ListView = {
    id: number;
    listKey: ListKey;
    baseViewKey: string;
    name: string;
    state: ListViewState;
    visibility: ListViewVisibility;
    owner: ListViewPerson;
    canEdit: boolean;
    /** The owner's only. */
    sharedEmployees: ListViewPerson[] | null;
};

export type ListViews = {
    views: ListView[];
    defaultListViewId: number | null;
    defaultBuiltInViewKey: string | null;
};

export type ListViewUpdate = {
    name: string;
    state: ListViewState;
    visibility: ListViewVisibility;
    sharedEmployeeIds: number[];
};

export type ListViewCreate = ListViewUpdate & { listKey: ListKey; baseViewKey: string };

export type ListViewDefault = { listViewId: number } | { builtInViewKey: string };
